import React, { HTMLAttributes, useState, useCallback, useRef } from 'react';
import '../../base.scss';
import './MultiSelect.scss';
import { Icon } from '../Icon';
import { classNames } from '../../utilities';
import useOutsideClick from '../../utilities/hooks/use-outside-click';
import { Checkbox } from '../Checkbox';
import { SearchBox } from '../SearchBox';
import { Button } from '../Button';

export interface MultiSelectOptionProps {
  /** display text for the object */
  text: string;
  /** display count for the object */
  count?: number;
  /** is option disabled or not
   * @default false
   */
  disabled?: boolean;
}

export interface MultiSelectProps extends HTMLAttributes<HTMLElement> {
  /** required for accessibility if no labels */
  'aria-label'?: string;
  /** Label to be displayed - component will append number of selected options */
  label: string;
  /** Options for the dropdown to display
   * @default []
   */
  options?: Array<MultiSelectOptionProps>;
  /** List of selected options
   * @default []
   */
  selectedOptions?: string[];
  /** selection is not clickable
   * @default false
   */
  disabled?: boolean;
  /** ID for the component */
  id?: string;
  /** error message from the parent if there is an error, leave empty if no error
   * @default null
   */
  error?: string;
  /** Loading boolean to be passed in from parent
   * @default false
   */
  isLoading?: boolean;
  /** set to true to display inline style instead
   * @default false
   */
  inline?: boolean;
  /** function to handle "Apply" button click */
  onApply?: (selected: string[]) => void;
  /** function to handle changing the selection */
  onSelectionChange?: (value: string, checked: boolean) => void;
  /** Function to clear all selected options */
  onClear?: () => void;
  /** Function to be called when user changes the selection, do not click Apply and clicks outside the component */
  onCancel?: (previousSelected: string[]) => void;
  /** Set to true to allow searching dropdown items*/
  isSearchable?: boolean;
  /* What is entered into the search box upon render */
  searchValue?: string;
  /* Function that gets called when input is cleared */
  onClearSearch?: () => void;
  /* Function that gets called whenever input changes */
  onSearchChange?: () => void;
  /* Custom hint text */
  placeholder?: string;
}
const MultiSelect = (props: MultiSelectProps) => {
  const {
    id,
    label,
    options = [],
    selectedOptions,
    onSelectionChange,
    onApply,
    onClear,
    onCancel,
    disabled = false,
    isLoading = false,
    inline = false,
    error,
    isSearchable,
    searchValue,
    onClearSearch,
    onSearchChange,
    placeholder,
  } = props;

  /** Apply button disabled when control first renders, and then will
   *  be enabled whenever the user selects or deselects something, and
   *  then disabled again when user applies their changes. */
  const [canApply, setCanApply] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [uncontrolledSelectedOptions, setUncontrolledSelectedOptions] = useState<string[]>([]);
  const [lastSavedOptions, setLastSavedOptions] = useState<string[]>([]);

  const currentSelectedOptions: string[] = selectedOptions ? selectedOptions : uncontrolledSelectedOptions;

  if (selectedOptions !== undefined && (!onClear || !onCancel)) {
    throw new Error(
      'For controlled component, parent component needs to provide an implementation for onClear and onCancel.'
    );
  }

  const ref = useRef(null);

  /** listens to see if the user clicks off of the dropdown
   *  closes the dropdown if they do
   */
  useOutsideClick(ref, () => {
    setExpanded(false);
    // should set the selected list to its previous state
    selectedOptions ? onCancel && onCancel(lastSavedOptions) : setUncontrolledSelectedOptions(lastSavedOptions);
  });

  /* Maintains the state of selection - uncontrolled component */
  const onUncontrolledSelectionChange = (value: string, checked: boolean) => {
    let newList = [...currentSelectedOptions];
    if (checked) {
      if (!newList.some((item) => item === value)) {
        newList.push(value);
      }
    } else {
      newList = newList.filter((item) => item !== value);
    }
    setUncontrolledSelectedOptions(newList);
  };

  /** sets state to open and close the menu */
  const toggleMenu = useCallback(() => {
    if (isSearchable && expanded) return;
    setExpanded(!expanded);
  }, [expanded]);

  const onChange = onSelectionChange ? onSelectionChange : onUncontrolledSelectionChange;
  const handleChange = (value: string, checked: boolean) => {
    setCanApply(true);
    onChange(value, checked);
  };

  const handleApply = () => {
    setCanApply(false);
    setLastSavedOptions(currentSelectedOptions);
    onApply && onApply(currentSelectedOptions);
    toggleMenu();
  };

  const handleClear = () => {
    setCanApply(true);
    onClear ? onClear() : setUncontrolledSelectedOptions([]);
  };

  const listboxItems = options?.map((option, index) => {
    if (option.disabled) {
      return (
        <Checkbox
          className="ps-multiselect__option--disabled"
          label={option.text}
          key={`${option.text}${index}`}
          disabled={option.disabled}
        />
      );
    }
    return (
      <Checkbox
        className="ps-multiselect__option"
        label={option.text}
        value={option.text}
        key={`${option.text}${index}`}
        disabled={option.disabled}
        count={option.count}
        checked={currentSelectedOptions?.some((s) => s === option.text)}
        onChange={(event, newVal) => {
          handleChange(event.target.value, newVal ?? false);
        }}
      />
    );
  });

  const renderNoResultsMsg = () => {
    return <p className="ps-multiselect__option--empty">No results found</p>;
  };

  const menuClassNames = classNames('ps-multiselect__menu', expanded ? 'ps-multiselect__menu--expanded' : '');

  const buttonClassNames = classNames(
    inline ? 'ps-multiselect__button-inline' : 'ps-multiselect__button',
    inline && error ? 'ps-select__button-inline--error' : '',
    disabled ? `ps-multiselect__button${inline ? '-inline' : ''}--disabled` : '',
    error ? 'ps-multiselect__button--error' : ''
  );

  const menuFooterClassNames = classNames('ps-multiselect__footer', isLoading ? 'ps-multiselect__footer--loading' : '');

  const clearButtonClassNames = classNames(
    'ps-multiselect__clear',
    currentSelectedOptions.length === 0 ? `ps-multiselect__clear--hidden` : ''
  );

  const arrowClassNames = classNames(
    'ps-multiselect__arrow',
    inline ? `ps-multiselect__arrow--inline${error ? '-error' : ''}` : '',
    disabled ? `ps-multiselect__arrow--${inline ? 'inline-' : ''}disabled` : ''
  );

  const menuContainerClassNames = classNames(
    'ps-multiselect__menu--container',
    options.length === 0 ? 'ps-multiselect__menu--container-empty' : ''
  );
  /** optional messages */
  const errorMsg = error && <div className="ps-multiselect__error">{error}</div>;
  const loadingMsg = <div className="ps-multiselect__loading">Loading Options...</div>;

  return (
    <div className="ps-multiselect" ref={ref} id={id}>
      <button
        aria-label={props['aria-label']}
        aria-haspopup="listbox"
        aria-expanded={expanded}
        className={buttonClassNames}
        disabled={disabled}
        onClick={toggleMenu}
      >
        {isSearchable && expanded ? (
          <SearchBox
            aria-label="SearchBox"
            value={searchValue}
            placeholder={placeholder}
            onChange={onSearchChange}
            onClear={onClearSearch}
          />
        ) : (
          `${label}${currentSelectedOptions.length > 0 ? ` (${currentSelectedOptions.length} selected)` : ''}`
        )}
        <Icon className={arrowClassNames} name="SortDown" />
      </button>
      <div className={menuClassNames} role="listbox">
        <div className={menuContainerClassNames}>
          {isLoading ? loadingMsg : options.length === 0 ? renderNoResultsMsg() : listboxItems}
        </div>
        <div className={menuFooterClassNames}>
          <Button className={clearButtonClassNames} variant="link" onClick={handleClear}>
            Clear
          </Button>
          <Button className="ps-multiselect__apply" onClick={handleApply} disabled={!canApply}>
            Apply
          </Button>
        </div>
      </div>
      {!inline && errorMsg}
    </div>
  );
};

export { MultiSelect };
